import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {writeAdminListState} from './adminListStorage'
import {loadAdminListState} from './loadAdminListState'

export type UseAdminListStateOptions<TFilter> = {
  /** Unique key per list route, e.g. `trip-management` */
  storageKey: string
  getDefaultFilter: () => TFilter
  isDefaultFilter?: (filter: TFilter, searchQuery: string) => boolean
}

export function useAdminListState<TFilter>({
  storageKey,
  getDefaultFilter,
  isDefaultFilter,
}: UseAdminListStateOptions<TFilter>) {
  const defaultsRef = useRef(getDefaultFilter)
  defaultsRef.current = getDefaultFilter

  const loadSaved = useCallback(
    () => loadAdminListState<TFilter>(storageKey),
    [storageKey]
  )

  const [page, setPageState] = useState(() => loadSaved()?.page ?? 1)
  const [searchQuery, setSearchQueryState] = useState(
    () => loadSaved()?.searchQuery ?? ''
  )
  const [activeFilter, setActiveFilterState] = useState<TFilter>(
    () => loadSaved()?.activeFilter ?? defaultsRef.current()
  )

  /** Re-apply sessionStorage when returning from detail routes (list remounts). */
  useLayoutEffect(() => {
    const saved = loadSaved()
    if (!saved?.activeFilter) return
    setPageState(saved.page)
    setSearchQueryState(saved.searchQuery)
    setActiveFilterState(saved.activeFilter)
  }, [loadSaved])

  useEffect(() => {
    writeAdminListState(storageKey, {page, searchQuery, activeFilter})
  }, [storageKey, page, searchQuery, activeFilter])

  const setPage = useCallback((p: number) => {
    setPageState(p)
  }, [])

  const setSearchQuery = useCallback((q: string) => {
    setSearchQueryState(q)
  }, [])

  const setActiveFilter = useCallback((filter: TFilter) => {
    setActiveFilterState(filter)
  }, [])

  const applyFilter = useCallback((filter: TFilter) => {
    setPageState(1)
    setActiveFilterState(filter)
  }, [])

  const resetToDefault = useCallback(() => {
    setPageState(1)
    setSearchQueryState('')
    setActiveFilterState(defaultsRef.current())
  }, [])

  const resetPageForSearch = useCallback(() => {
    setPageState(1)
  }, [])

  const isFilterActive = isDefaultFilter
    ? !isDefaultFilter(activeFilter, searchQuery)
    : false

  return {
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    activeFilter,
    setActiveFilter,
    applyFilter,
    resetToDefault,
    resetPageForSearch,
    isFilterActive,
  }
}
